import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { X, Handshake, MessageCircle, ExternalLink, ChevronLeft, ChevronRight, BadgePercent } from "lucide-react";
import { useLang } from "@/i18n";
import { api } from "@/lib/api";
import PartnerCodePopup from "@/components/PartnerCodePopup";

const OWNER_WHATSAPP = "50586156364";
const BACKEND = process.env.REACT_APP_BACKEND_URL;

function BillboardCard({ b, t, onShowCode }) {
  const hasCode = !!(b.discount_code || "").trim();
  const clickable = hasCode || !!b.link;
  const activate = () => {
    if (hasCode) { onShowCode(b); return; }
    if (b.link) { api.trackHomeBillboardClick(b.id); window.open(b.link, "_blank", "noopener,noreferrer"); }
  };
  const tf = b.transform || { zoom: 1, offset_x: 0, offset_y: 0, fit: "cover" };
  const mediaStyle = {
    width: "100%", height: "100%", objectFit: tf.fit,
    transform: `scale(${tf.zoom}) translate(${tf.offset_x * 50}%, ${tf.offset_y * 50}%)`,
    transformOrigin: "center",
  };
  return (
    <div
      data-testid={`home-billboard-${b.id}`}
      className="snap-center shrink-0 w-full rounded-2xl overflow-hidden border-2 border-[#FFB300]/40 bg-black shadow-[0_8px_30px_rgba(0,0,0,0.5)]"
    >
      {hasCode && (
        <button
          data-testid={`home-billboard-code-banner-${b.id}`}
          onClick={activate}
          className="w-full bg-[#FFD400] text-black px-3 py-2.5 flex items-center justify-center gap-2 hover:brightness-105"
        >
          <BadgePercent size={18} strokeWidth={2.5} className="shrink-0" />
          <span className="font-pixel text-sm md:text-base tracking-widest break-all">{t("partnerCodeLabel")}: {b.discount_code}</span>
        </button>
      )}
      <div
        className={`relative w-full aspect-video bg-black ${clickable ? "cursor-pointer" : ""}`}
        onClick={clickable ? activate : undefined}
      >
        {b.has_video && b.video_url ? (
          <video src={`${BACKEND}${b.video_url}`} style={mediaStyle} muted loop autoPlay playsInline />
        ) : b.has_image && b.image_url ? (
          <img
            src={`${BACKEND}${b.image_url}`}
            alt={b.label || "partner"}
            style={mediaStyle}
            loading="eager"
            draggable={false}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><span className="font-pixel text-white/30 text-xs">{b.label || "PARTNER"}</span></div>
        )}
        {b.label && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/65 backdrop-blur font-pixel text-[9px] text-[#FFEB3B]">{b.label}</span>
        )}
      </div>
      {hasCode ? (
        <button
          data-testid={`home-billboard-showcode-${b.id}`}
          onClick={activate}
          className="pixel-btn bg-[#FFB300] text-black w-full py-3 text-xs flex items-center justify-center gap-2"
        >
          <BadgePercent size={16} strokeWidth={2.5} /> {t("partnerShowCode")}
        </button>
      ) : clickable ? (
        <button
          data-testid={`home-billboard-cta-${b.id}`}
          onClick={activate}
          className="pixel-btn bg-[#FFB300] text-black w-full py-3 text-xs flex items-center justify-center gap-2"
        >
          <ExternalLink size={16} strokeWidth={2.5} /> {b.cta || t("brandPartnersVisit")}
        </button>
      ) : null}
    </div>
  );
}

export default function SponsorModal({ onClose }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", whatsapp: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [billboards, setBillboards] = useState([]);
  const [codePopup, setCodePopup] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => { api.homeBillboards().then((r) => setBillboards(r.data || [])).catch(() => setBillboards([])); }, []);

  const showCode = (b) => { api.trackHomeBillboardCodeClick(b.id); setCodePopup(b); };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const scrollBy = (dir) => {
    const el = scrollRef.current;
    if (!el || !el.children.length) return;
    const cardW = el.children[0].getBoundingClientRect().width + 12; // includes gap-3
    const idx = Math.round(el.scrollLeft / cardW);
    const next = Math.max(0, Math.min(el.children.length - 1, idx + dir));
    el.scrollTo({ left: next * cardW, behavior: "smooth" });
  };

  const submit = async () => {
    if (sending || !form.name.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      await api.createSponsor(form);
      toast.success(t("sponsorThanks"));
      onClose();
    } catch (e) {
      toast.error("Error");
    } finally {
      setSending(false);
    }
  };

  const hasAds = billboards.length > 0;

  return (
    <div className="fixed inset-0 z-[92] flex items-start justify-center bg-black/85 p-2 sm:p-4 overflow-y-auto overscroll-contain safe-x safe-t safe-b" data-testid="sponsor-modal">
      <div className={`pixel-card bg-[#141414] w-full ${hasAds ? "max-w-4xl" : "max-w-sm"} max-h-full p-3 sm:p-5 pop-in relative flex flex-col landscape:flex-row gap-3 sm:gap-5 overflow-y-auto my-auto`}>
        <button data-testid="sponsor-close-btn" onClick={onClose} className="pixel-btn bg-black/55 text-white p-2 absolute top-2 right-2 z-20" aria-label="Close">
          <X size={16} strokeWidth={2.5} />
        </button>

        {/* Ads column — large, horizontally scrollable carousel */}
        {hasAds && (
          <div className="flex flex-col min-h-0 landscape:flex-[2] landscape:min-w-0" data-testid="home-billboards">
            <h3 className="font-pixel text-[11px] text-[#FFB300] pixel-stroke mb-2 shrink-0">{t("brandPartnersHeading")}</h3>
            <div className="relative flex-1 min-h-0 flex items-center">
              <div
                ref={scrollRef}
                className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-none py-1 w-full"
                style={{ scrollbarWidth: "none" }}
              >
                {billboards.map((b) => <BillboardCard key={b.id} b={b} t={t} onShowCode={showCode} />)}
              </div>
              {billboards.length > 1 && (
                <>
                  <button onClick={() => scrollBy(-1)} data-testid="home-billboards-prev" aria-label="Previous" className="flex absolute left-1 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FFB300] text-black items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.5)] ring-2 ring-black/25 hover:bg-[#ffc233] active:scale-90 transition-transform">
                    <ChevronLeft size={22} strokeWidth={3} />
                  </button>
                  <button onClick={() => scrollBy(1)} data-testid="home-billboards-next" aria-label="Next" className="flex absolute right-1 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-[#FFB300] text-black items-center justify-center shadow-[0_4px_12px_rgba(0,0,0,0.5)] ring-2 ring-black/25 hover:bg-[#ffc233] active:scale-90 transition-transform">
                    <ChevronRight size={22} strokeWidth={3} />
                  </button>
                </>
              )}
            </div>
            {billboards.length > 1 && (
              <div className="flex items-center justify-center gap-2 mt-2 shrink-0 text-white/55" data-testid="brand-swipe-hint">
                <ChevronLeft size={15} strokeWidth={2.5} className="animate-pulse" />
                <span className="font-vt text-sm">{t("brandPartnersSwipe", "Swipe to see more")}</span>
                <ChevronRight size={15} strokeWidth={2.5} className="animate-pulse" />
              </div>
            )}
          </div>
        )}

        {/* Form column */}
        <div className="flex flex-col min-h-0 overflow-y-auto landscape:flex-[1] landscape:max-w-xs">
          <div className="flex flex-col items-center text-center mb-3">
            <div className="w-11 h-11 rounded-2xl bg-[#FF1744] flex items-center justify-center mb-2">
              <Handshake size={24} strokeWidth={2.5} className="text-white" />
            </div>
            <h2 className="font-pixel text-sm sm:text-base text-[#FFEB3B] pixel-stroke">{t("sponsorTitle")}</h2>
            <p className="font-vt text-base text-white/80 mt-1.5 leading-tight">{t("sponsorIntro")}</p>
          </div>

          <div className="flex flex-col gap-2.5">
            <input data-testid="sponsor-name" value={form.name} onChange={set("name")} placeholder={t("sponsorName")} maxLength={80}
              className="font-vt text-lg bg-white text-black rounded-xl px-4 py-2.5 outline-none focus:ring-2 ring-[#FF1744]" />
            <input data-testid="sponsor-company" value={form.company} onChange={set("company")} placeholder={t("sponsorCompany")} maxLength={120}
              className="font-vt text-lg bg-white text-black rounded-xl px-4 py-2.5 outline-none focus:ring-2 ring-[#FF1744]" />
            <input data-testid="sponsor-whatsapp" type="tel" value={form.whatsapp} onChange={set("whatsapp")} placeholder={t("sponsorWhatsapp")} maxLength={40}
              className="font-vt text-lg bg-white text-black rounded-xl px-4 py-2.5 outline-none focus:ring-2 ring-[#FF1744]" />
            <input data-testid="sponsor-email" type="email" value={form.email} onChange={set("email")} placeholder={t("sponsorEmail")} maxLength={120}
              className="font-vt text-lg bg-white text-black rounded-xl px-4 py-2.5 outline-none focus:ring-2 ring-[#FF1744]" />
            <textarea data-testid="sponsor-message" value={form.message} onChange={set("message")} placeholder={t("sponsorMessage")} maxLength={1000} rows={2}
              className="font-vt text-lg bg-white text-black rounded-xl px-4 py-2.5 outline-none focus:ring-2 ring-[#FF1744] resize-none" />
            <button data-testid="sponsor-submit-btn" onClick={submit} disabled={sending || !form.name.trim() || !form.message.trim()}
              className="pixel-btn bg-[#FF1744] text-white w-full py-3 text-sm disabled:opacity-50">
              {sending ? "..." : t("sponsorSend")}
            </button>
            <a
              data-testid="sponsor-whatsapp-cta"
              href={`https://wa.me/${OWNER_WHATSAPP}`}
              target="_blank"
              rel="noreferrer"
              className="pixel-btn bg-[#25D366] text-white w-full py-2.5 text-sm flex items-center justify-center gap-2 no-underline"
            >
              <MessageCircle size={18} strokeWidth={2.5} /> {t("sponsorWhatsCta")}
            </a>
          </div>
        </div>
      </div>
      {codePopup && <PartnerCodePopup billboard={codePopup} onClose={() => setCodePopup(null)} />}
    </div>
  );
}
